import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useTemporaryMessage, { TemporaryMessageTypes } from '@/hooks/useTemporaryMessage';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/SupabaseAuthProvider';
import SignInComponent from './SignIn.component';

type Credentials = { email: string; password: string };

const SignInContainer = function (): JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated, signInWithPassword, signInWithGoogle } = useAuth();
  const { setTemporaryMessageInMs, temporaryMessage, temporaryMessageType } = useTemporaryMessage();
  const [credentials, setCredentials] = useState<Credentials>({ email: '', password: '' });
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const handleChangeInputs = (input: React.ChangeEvent<HTMLInputElement>): void => {
    setCredentials((state) => ({ ...state, [input.target.name]: input.target.value }));
  };

  const handleLoginWithCredentials = useCallback(async (): Promise<void> => {
    if (!credentials.email || !credentials.password) {
      setTemporaryMessageInMs({
        message: 'Please fill all fields to sign in',
        displayTimeoutInMs: 5000,
      });
      return;
    }

    setLoading(true);
    const { error } = await signInWithPassword(credentials.email, credentials.password);
    setLoading(false);

    if (error) {
      setTemporaryMessageInMs({
        message: error.message || 'Invalid credentials',
        displayTimeoutInMs: 5000,
      });
      return;
    }

    // Check if user must reset password (created with temporary password)
    const {
      data: { user: authUser },
    } = await supabase.auth.getUser();
    if (authUser?.user_metadata?.must_reset_password) {
      navigate('/reset-password');
    }
  }, [credentials, signInWithPassword, setTemporaryMessageInMs, navigate]);

  const handleGoogleLogin = (): void => {
    signInWithGoogle();
  };

  const handleRequestChangePassword = async (): Promise<void> => {
    if (!credentials.email) {
      setTemporaryMessageInMs({
        message: 'Enter your email address first, then click Forgot password',
        displayTimeoutInMs: 5000,
      });
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(credentials.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      setTemporaryMessageInMs({ message: error.message, displayTimeoutInMs: 5000 });
    } else {
      setTemporaryMessageInMs({
        message: 'Check your email for the password reset link',
        displayTimeoutInMs: 10000,
        type: TemporaryMessageTypes.SUCCESS,
      });
    }
  };

  const submitFormByPressingEnter = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleLoginWithCredentials();
      }
    },
    [handleLoginWithCredentials]
  );

  useEffect(() => {
    document.addEventListener('keydown', submitFormByPressingEnter);
    return () => {
      document.removeEventListener('keydown', submitFormByPressingEnter);
    };
  }, [submitFormByPressingEnter]);

  return (
    <SignInComponent
      handleChangeInputs={handleChangeInputs}
      handleLoginWithCredentials={handleLoginWithCredentials}
      handleGoogleLogin={handleGoogleLogin}
      handleRequestChangePassword={handleRequestChangePassword}
      isLoading={isLoading}
      errorMessage={temporaryMessage}
      temporaryMessageType={temporaryMessageType}
    />
  );
};

export default SignInContainer;
